/**
 * OutlineReviewDialog Component
 *
 * Displays the AI-generated blog post outline for user approval or revision request.
 * Automatically opens when AI post reaches "awaiting_outline_approval" phase.
 *
 * Story: BLOG-AI-003, AC-16, AC-17, AC-18, Task 12
 */
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { apiService } from '@/services/api'
import ReactMarkdown from 'react-markdown'

interface BlogPost {
  id: string
  title: string
  ai_metadata?: {
    phase: string
    outline?: string
    outline_revision_count?: number
  }
}

interface OutlineReviewDialogProps {
  post: BlogPost | null
  open: boolean
  onClose: () => void
}

export function OutlineReviewDialog({ post, open, onClose }: OutlineReviewDialogProps) {
  const queryClient = useQueryClient()
  const [revisionFeedback, setRevisionFeedback] = useState('')

  const outline = post?.ai_metadata?.outline || ''
  const revisionCount = post?.ai_metadata?.outline_revision_count || 0

  // Approve outline mutation
  const approveMutation = useMutation({
    mutationFn: async () => {
      if (!post) throw new Error('No post selected')
      return apiService.blog.aiWriter.approveOutline(post.id)
    },
    onSuccess: () => {
      toast.success('Outline approved! AI is writing content now.')
      queryClient.invalidateQueries({ queryKey: ['blog-posts'] })
      onClose()
    },
    onError: (error: any) => {
      console.error('Failed to approve outline:', error)
      toast.error(error?.response?.data?.detail || 'Failed to approve outline')
    },
  })

  // Revise outline mutation
  const reviseMutation = useMutation({
    mutationFn: async () => {
      if (!post || !revisionFeedback.trim()) throw new Error('No feedback provided')
      return apiService.blog.aiWriter.reviseOutline(post.id, {
        feedback: revisionFeedback,
      })
    },
    onSuccess: () => {
      toast.success('Regenerating outline with your feedback...')
      queryClient.invalidateQueries({ queryKey: ['blog-posts'] })
      onClose()
      setRevisionFeedback('')
    },
    onError: (error: any) => {
      console.error('Failed to request revisions:', error)
      toast.error(error?.response?.data?.detail || 'Failed to request revisions')
    },
  })

  const handleApprove = () => {
    approveMutation.mutate()
  }

  const handleRevise = () => {
    if (!revisionFeedback.trim()) {
      toast.error('Please provide feedback for the revision')
      return
    }
    reviseMutation.mutate()
  }

  if (!post || !outline) return null

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-card text-card-foreground border-border sm:max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-foreground">Review AI-Generated Outline</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Review the outline below and approve it to continue, or request revisions.
            {revisionCount > 0 && (
              <span className="block mt-1 text-sm">
                (Revision #{revisionCount + 1})
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-4">
          {/* Outline Preview */}
          <div className="space-y-2">
            <Label className="text-base font-semibold text-foreground">
              Outline Preview
            </Label>
            <div className="rounded-lg border border-border bg-background p-6 prose prose-sm dark:prose-invert max-w-none">
              <ReactMarkdown>{outline}</ReactMarkdown>
            </div>
          </div>

          {/* Revision Feedback Section */}
          <div className="space-y-2">
            <Label htmlFor="revision-feedback" className="text-base font-semibold text-foreground">
              Request Changes (optional)
            </Label>
            <Textarea
              id="revision-feedback"
              value={revisionFeedback}
              onChange={(e) => setRevisionFeedback(e.target.value)}
              placeholder="E.g., Add more details about X, include a section on Y, expand the introduction..."
              rows={4}
              className="bg-background border-border text-foreground placeholder:text-muted-foreground focus:ring-ring"
            />
            {revisionFeedback.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {revisionFeedback.length} characters
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="flex gap-2 sm:justify-between">
          <Button
            variant="outline"
            onClick={handleRevise}
            disabled={!revisionFeedback.trim() || reviseMutation.isPending}
            className="border-border hover:bg-accent"
          >
            {reviseMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Requesting Revisions...
              </>
            ) : (
              'Request Revisions'
            )}
          </Button>

          <Button
            onClick={handleApprove}
            disabled={approveMutation.isPending}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {approveMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Approving...
              </>
            ) : (
              'Approve & Continue'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
